import type { OnDestroy } from '@angular/core';
import { Injectable, inject } from '@angular/core';
import { Assets } from '@freelancer/ui/assets';
import { fromEvent, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class Sounds implements OnDestroy {
  private assets = inject(Assets);

  lockKey = 'soundsLock';
  localStorageKey = 'soundsMasterTabId';

  tabId: number;
  lastNotified: number;

  messageSound: HTMLAudioElement;
  callingSound: HTMLAudioElement;
  readonly notificationWaitPeriod = 1000;

  private subscriptions = new Subscription();

  constructor() {
    this.tabId = Date.now() + window.performance.now();
    this.lastNotified = 0;

    this.handleEvent = this.handleEvent.bind(this);
    this.subscriptions.add(
      fromEvent(window, 'pagehide').subscribe(this.handleEvent),
    );
    this.subscriptions.add(
      fromEvent(window, 'visibilitychange').subscribe(this.handleEvent),
    );

    this.initialiseAsMaster();

    this.messageSound = new Audio(this.assets.getUrl('sounds/message.mp3'));
    if (this.messageSound) {
      this.messageSound.load();
    }
  }

  handleNotification(type: string): void {
    this.runWithLock(() => {
      let masterTabId = this.getMasterTabId();

      if (!masterTabId || document.hasFocus()) {
        masterTabId = this.tabId;
        this.setMasterTabId(this.tabId);
      }

      // Only play the sound in the master tab and at most once per notification
      // wait period
      if (
        this.lastNotified > Date.now() - this.notificationWaitPeriod ||
        masterTabId !== this.tabId
      ) {
        return;
      }
      if (type === 'message' && this.messageSound) {
        this.messageSound
          .play()
          .then(() => {
            this.lastNotified = Date.now();
          })
          .catch(error => {
            // Autoplay was prevented.
            console.warn(error);
          });
      }
    });
  }

  playCallingSound(): void {
    this.runWithLock(() => {
      let masterTabId = this.getMasterTabId();

      if (!masterTabId || document.hasFocus()) {
        masterTabId = this.tabId;
        this.setMasterTabId(this.tabId);
      }

      // Only play the sound in the master tab
      if (masterTabId !== this.tabId) {
        return;
      }

      this.callingSound = new Audio(
        this.assets.getUrl('sounds/videochat-calling.mp3'),
      );

      this.callingSound.loop = true;
      this.callingSound.load();
      this.callingSound.play();
    });
  }

  /**
   * Stop the dialing sound. See call-window.component line 1343
   */
  stopCallingSound(): void {
    if (this.callingSound) {
      this.callingSound.src = '';
      this.callingSound.pause();
      setTimeout(() => this.callingSound.load());
    }
  }

  /**
   * Set the current tab as the master tab (i.e. the tab to play
   * the sound from)
   */
  private initialiseAsMaster(): void {
    this.runWithLock(() => {
      const masterTabId = this.getMasterTabId();
      if (!masterTabId || document.hasFocus()) {
        this.setMasterTabId(this.tabId);
      }
    });
  }

  private handleEvent(event: Event): void {
    this.runWithLock(() => {
      // Set the crrent tab as the master tab when it is visible
      if (
        event.type === 'visibilitychange' &&
        document.visibilityState === 'visible'
      ) {
        this.setMasterTabId(this.tabId);
      }

      // Unset the master tab when current page/tab is hidden
      else if (
        event.type === 'pagehide' &&
        this.getMasterTabId() === this.tabId
      ) {
        this.setMasterTabId(undefined);
      }
    });
  }

  private getMasterTabId(): number | undefined {
    // Using localStorage directly to avoid some race condition
    // issues in LocalStorage service
    const masterTabId = window.localStorage.getItem(this.localStorageKey);
    try {
      return masterTabId ? JSON.parse(masterTabId) : undefined;
    } catch (e: unknown) {
      return undefined;
    }
  }

  private setMasterTabId(masterTabId: number | undefined): void {
    // Using localStorage directly to avoid some race condition
    // issues in LocalStorage service
    try {
      window.localStorage.setItem(
        this.localStorageKey,
        JSON.stringify(masterTabId),
      );
    } catch (e: unknown) {
      // Do nothing
    }
  }

  /**
   * Run a function with a lock on supported browsers to avoid a
   * race condition where multiple tabs try to write/read from
   * localStorage at the same time
   */
  private runWithLock(fn: () => void): void {
    if ('locks' in navigator) {
      navigator.locks.request(this.lockKey, fn);
    } else {
      fn();
    }
  }

  /**
   * Checks if the browser can play audio
   *
   * @privateRemarks
   *
   * TODO: Apply this check to other methods in the `Sounds` service to prevent
   * the service from running on incompatible browsers
   *
   * FIXME: The browser having audio capabilities does not guarantee a sound to
   * be played/playable; we should improve that check and/or find a way to
   * handle those play() errors nicely.
   */
  isAudioCapable(): boolean {
    return !!window.AudioContext || !!window.webkitAudioContext;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
